import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Package, ShoppingBag, Star, Edit3, Trash2, Plus, X, Check, CheckCircle, Store,
  TrendingUp, Clock, Truck, Upload, AlertCircle, Lock, MessageCircle,
  Phone, BarChart2, Zap
} from "lucide-react";
import API, { formatError, getImageUrl } from "../api";
import { useAuth } from "../contexts/AuthContext";
import { StarRating, formatPrice } from "../components/ProductCard";
import SubscriptionModal from "../components/SubscriptionModal";
import { formatWhatsApp } from "../utils/whatsapp";

const ADMIN_WA = "33777369462";

// Push notifications banner
function PushNotificationBanner() {
  const [status, setStatus] = useState(() => {
    if (!("Notification" in window) || !("serviceWorker" in navigator)) return "unsupported";
    return Notification.permission === "granted" ? "granted" : Notification.permission === "denied" ? "denied" : "default";
  });
  const [subscribing, setSubscribing] = useState(false);

  const subscribe = async () => {
    setSubscribing(true);
    try {
      const perm = await Notification.requestPermission();
      if (perm !== "granted") { setStatus("denied"); return; }
      const reg = await navigator.serviceWorker.ready;
      const vapidKeyResp = await API.get("/api/push/vapid-public-key");
      const vapidKey = vapidKeyResp.data.vapid_public_key;
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: vapidKey
      });
      const subData = sub.toJSON();
      await API.post("/api/push/subscribe", { endpoint: subData.endpoint, keys: subData.keys });
      setStatus("granted");
    } catch (e) {
      console.error("Push subscription failed:", e);
    } finally {
      setSubscribing(false);
    }
  };

  if (status === "granted" || status === "unsupported") return null;

  return (
    <div className="bg-blue-50 border border-blue-100 rounded-xl p-3 mb-4 flex items-center justify-between gap-3">
      <div className="flex items-start gap-2.5">
        <Zap size={16} className="text-blue-500 mt-0.5 shrink-0" />
        <div>
          <p className="text-xs font-semibold text-blue-800">Activez les notifications push</p>
          <p className="text-xs text-blue-600">Soyez alerté instantanément pour chaque nouveau client</p>
        </div>
      </div>
      {status === "denied" ? (
        <p className="text-xs text-red-500 shrink-0">Bloqué dans votre navigateur</p>
      ) : (
        <button
          onClick={subscribe}
          disabled={subscribing}
          className="shrink-0 bg-blue-600 text-white text-xs font-bold px-3 py-1.5 rounded-lg hover:bg-blue-700 disabled:opacity-60 transition-colors"
          data-testid="enable-push-btn"
        >
          {subscribing ? "..." : "Activer"}
        </button>
      )}
    </div>
  );
}

// Machine à états — transitions autorisées pour le vendeur
const VENDOR_NEXT_STATES = {
  initiated: [
    { value: "en_livraison", label: "En livraison", danger: false },
    { value: "annulé", label: "Annuler", danger: true },
  ],
  payment_declared: [
    { value: "en_livraison", label: "En livraison", danger: false },
    { value: "annulé", label: "Annuler", danger: true },
  ],
  payment_confirmed: [
    { value: "en_livraison", label: "En livraison", danger: false },
    { value: "annulé", label: "Annuler", danger: true },
  ],
  approved: [{ value: "paid", label: "Marquer Payé", danger: false }],
  paid: [
    { value: "en_livraison", label: "En livraison", danger: false },
    { value: "annulé", label: "Annuler", danger: true },
  ],
  en_livraison: [
    { value: "livré", label: "Livré !", danger: false },
    { value: "annulé", label: "Annuler", danger: true },
  ],
};

const STATUS_LABELS = {
  pending: "En attente", initiated: "Initiée", awaiting_payment: "Paiement requis",
  payment_declared: "Paiement déclaré", payment_confirmed: "Paiement confirmé",
  approved: "Approuvé", paid: "Payé", en_livraison: "En livraison",
  livré: "Livré", annulé: "Annulé"
};
const STATUS_COLORS = {
  pending: "bg-yellow-100 text-yellow-700", initiated: "bg-yellow-100 text-yellow-700",
  awaiting_payment: "bg-blue-100 text-blue-700",
  payment_declared: "bg-teal-100 text-teal-700",
  payment_confirmed: "bg-green-100 text-green-700", approved: "bg-green-100 text-green-700",
  paid: "bg-blue-100 text-blue-700", en_livraison: "bg-orange-100 text-orange-700",
  livré: "bg-green-100 text-green-700", annulé: "bg-red-100 text-red-700"
};

const CATEGORIES_LIST = [
  { slug: "electronique", name: "Électronique" },
  { slug: "mode-vetements", name: "Mode & Vêtements" },
  { slug: "alimentation", name: "Alimentation" },
  { slug: "maison-decoration", name: "Maison & Déco" },
  { slug: "beaute-sante", name: "Beauté & Santé" },
  { slug: "sports-loisirs", name: "Sports & Loisirs" },
  { slug: "services", name: "Services" },
  { slug: "autres", name: "Autres" },
];

export default function VendorDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const [profile, setProfile] = useState(null);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [categories, setCategories] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showProductForm, setShowProductForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [productForm, setProductForm] = useState({ name: "", description: "", price: "", stock: "1", category_id: "", images: [] });
  const [uploadingImg, setUploadingImg] = useState(false);
  const [saving, setSaving] = useState(false);
  const [profileForm, setProfileForm] = useState({});
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileMsg, setProfileMsg] = useState("");
  const [showSubModal, setShowSubModal] = useState(false);
  const [subStatus, setSubStatus] = useState(null);
  const fileRef = useRef();

  useEffect(() => {
    if (!user) { navigate("/auth"); return; }
    if (user.role === "admin") { navigate("/dashboard/admin"); return; }
    Promise.all([
      API.get("/api/vendors/my/profile"),
      API.get("/api/products/my/list"),
      API.get("/api/orders"),
      API.get("/api/categories"),
      API.get("/api/vendor/subscription-status"),
      API.get("/api/vendor/analytics"),
    ]).then(([p, prods, ords, cats, sub, ana]) => {
      setProfile(p.data);
      setProfileForm(p.data);
      setProducts(prods.data || []);
      setOrders(ords.data || []);
      setCategories(cats.data || []);
      setSubStatus(sub.data);
      setAnalytics(ana.data);
    }).catch(console.error).finally(() => setLoading(false));
  }, [user, navigate]);

  const stats = {
    products: products.length,
    orders: orders.length,
    pending: orders.filter((o) => ["pending", "awaiting_payment", "initiated"].includes(o.status)).length,
    revenue: orders.filter((o) => ["paid", "livré"].includes(o.status)).reduce((s, o) => s + (o.total_amount || 0), 0),
  };

  const handleUploadImage = async (file) => {
    setUploadingImg(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await API.post("/api/upload", form);
      setProductForm((prev) => ({ ...prev, images: [...prev.images, data.url] }));
    } catch (e) {
      alert(formatError(e));
    } finally {
      setUploadingImg(false);
    }
  };

  const handleSaveProduct = async () => {
    if (!productForm.name || !productForm.price) return;
    setSaving(true);
    try {
      const payload = { ...productForm, price: parseFloat(productForm.price), stock: parseInt(productForm.stock) };
      if (editingProduct) {
        const { data } = await API.put(`/api/products/${editingProduct._id}`, payload);
        setProducts((prev) => prev.map((p) => p._id === editingProduct._id ? data : p));
      } else {
        const { data } = await API.post("/api/products", payload);
        setProducts((prev) => [data, ...prev]);
      }
      setShowProductForm(false);
      setEditingProduct(null);
      setProductForm({ name: "", description: "", price: "", stock: "1", category_id: "", images: [] });
    } catch (e) {
      alert(formatError(e));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteProduct = async (id) => {
    if (!window.confirm("Supprimer ce produit ?")) return;
    await API.delete(`/api/products/${id}`);
    setProducts((prev) => prev.filter((p) => p._id !== id));
  };

  const handleSaveProfile = async () => {
    setSavingProfile(true);
    try {
      const { data } = await API.put("/api/vendors/my/profile", profileForm);
      setProfile(data);
      setProfileMsg("Profil mis à jour avec succès !");
      setTimeout(() => setProfileMsg(""), 3000);
    } catch (e) {
      setProfileMsg(formatError(e));
    } finally {
      setSavingProfile(false);
    }
  };

  const openEdit = (product) => {
    setEditingProduct(product);
    setProductForm({ name: product.name, description: product.description, price: String(product.price), stock: String(product.stock), category_id: product.category_id || "", images: product.images || [] });
    setShowProductForm(true);
  };

  if (loading) return (
    <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center">
      <div className="animate-spin rounded-full h-10 w-10 border-4 border-[#FF9900] border-t-transparent" />
    </div>
  );

  if (!profile?.is_approved) {
    return (
      <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center px-4 pb-24">
        <div className="bg-white border border-amber-200 rounded-2xl p-8 max-w-md w-full shadow-sm">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Clock size={32} className="text-amber-500" />
            </div>
            <h2 className="text-xl font-extrabold text-[#232F3E] mb-2">Boutique en cours de validation</h2>
            <p className="text-gray-500 text-sm leading-relaxed">
              Votre boutique <span className="font-bold text-[#232F3E]">{profile?.shop_name || user?.name}</span> est en cours d'examen par notre équipe. Ce processus prend généralement moins de 24h.
            </p>
          </div>

          <div className="space-y-3 mb-6">
            <div className="flex items-start gap-3 p-3 bg-green-50 rounded-xl">
              <CheckCircle size={16} className="text-green-500 mt-0.5 shrink-0" />
              <div>
                <p className="text-sm font-semibold text-gray-800">Compte créé avec succès</p>
                <p className="text-xs text-gray-500">Vos informations ont bien été enregistrées</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 bg-amber-50 rounded-xl border border-amber-100">
              <Clock size={16} className="text-amber-500 mt-0.5 shrink-0" />
              <div>
                <p className="text-sm font-semibold text-gray-800">Validation admin en attente</p>
                <p className="text-xs text-gray-500">Notre équipe vérifie votre boutique</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-xl opacity-60">
              <Store size={16} className="text-gray-400 mt-0.5 shrink-0" />
              <div>
                <p className="text-sm font-semibold text-gray-600">Publication de vos produits</p>
                <p className="text-xs text-gray-400">Disponible après validation</p>
              </div>
            </div>
          </div>

          <p className="text-xs text-gray-400 text-center mb-4">
            Pour accélérer la validation, contactez l'administrateur directement sur WhatsApp :
          </p>
          <a
            href={`https://wa.me/${ADMIN_WA}?text=${encodeURIComponent(`Bonjour Jokkoleen, mon compte vendeur "${profile?.shop_name || user?.name}" est en attente de validation. Pouvez-vous l'activer ?`)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="w-full inline-flex items-center justify-center gap-2 bg-[#25D366] text-white font-bold py-3 rounded-xl hover:bg-[#20BD5A] transition-colors text-sm"
            data-testid="contact-admin-whatsapp-btn"
          >
            <MessageCircle size={16} /> Contacter l'admin sur WhatsApp
          </a>
          <p className="text-xs text-gray-400 text-center mt-3">
            Numéro WhatsApp de votre boutique : <span className="font-semibold">{user?.whatsapp}</span>
          </p>
        </div>
      </div>
    );
  }

  const planConfig = {
    FREE: { label: "Gratuit", color: "bg-gray-100 text-gray-700", leadText: `${profile?.leads_count || 0}/${profile?.leads_limit || 5} leads utilisés`, blocked: (profile?.leads_count || 0) >= (profile?.leads_limit || 5) },
    PRO: { label: "PRO", color: "bg-blue-100 text-blue-700", leadText: "Leads illimités", blocked: false },
    PREMIUM: { label: "PREMIUM", color: "bg-purple-100 text-purple-700", leadText: "Leads illimités + Visibilité max", blocked: false },
  };
  const currentPlan = planConfig[profile?.plan || "FREE"];
  const isBlocked = currentPlan.blocked;

  const TABS = [
    { id: "overview", label: "Aperçu", icon: TrendingUp },
    { id: "products", label: `Produits (${stats.products})`, icon: Package },
    { id: "orders", label: `Commandes (${stats.orders})`, icon: ShoppingBag },
    { id: "profile", label: "Ma boutique", icon: Store },
  ];

  return (
    <div className="bg-[#F9FAFB] min-h-screen pb-24 md:pb-8">
      <div className="w-full max-w-5xl mx-auto px-4 py-4">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-xl font-extrabold text-[#232F3E]">Tableau de bord</h1>
            <p className="text-sm text-gray-500">{profile?.shop_name}</p>
          </div>
          <Link to={`/boutique/${user?._id}`} className="text-xs text-green-600 hover:underline flex items-center gap-1" data-testid="view-shop-link">
            Voir ma boutique
          </Link>
        </div>

        {/* Plan banner */}
        <div className={`rounded-xl p-4 mb-4 flex items-center justify-between ${isBlocked ? "bg-red-50 border border-red-200" : "bg-green-50 border border-green-200"}`} data-testid="plan-banner">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className={`text-xs font-bold px-2 py-0.5 rounded ${currentPlan.color}`}>{currentPlan.label}</span>
              {isBlocked && <span className="text-xs text-red-600 font-bold">Limite atteinte</span>}
            </div>
            <p className="text-xs text-gray-600">{currentPlan.leadText}</p>
            {isBlocked && <p className="text-xs text-red-600 mt-0.5">Vous ne pouvez plus voir les nouveaux clients</p>}
          </div>
          <div className="flex items-center gap-2">
            {profile?.plan === "FREE" && (
              <button
                onClick={() => setShowSubModal(true)}
                className="bg-green-600 text-white font-bold px-3 py-2 rounded-lg text-xs hover:bg-green-700 whitespace-nowrap"
                data-testid="upgrade-btn"
              >
                {isBlocked ? "Passer en PRO" : "Passer en PRO"}
              </button>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex overflow-x-auto gap-1 mb-5 bg-white rounded-md border border-gray-200 p-1">
          {TABS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              className={`flex items-center gap-1.5 shrink-0 px-3 py-2 rounded text-xs font-semibold transition-colors ${tab === id ? "bg-[#232F3E] text-white" : "text-gray-600 hover:bg-gray-50"}`}
              data-testid={`vendor-tab-${id}`}
            >
              <Icon size={13} /> {label}
            </button>
          ))}
        </div>

        {/* Overview */}
        {tab === "overview" && (
          <div>
            {/* Push notification banner */}
            <PushNotificationBanner />
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
              {[
                { label: "Produits", value: stats.products, icon: Package, color: "bg-blue-50 text-blue-600" },
                { label: "Commandes", value: stats.orders, icon: ShoppingBag, color: "bg-purple-50 text-purple-600" },
                { label: "En attente", value: stats.pending, icon: Clock, color: "bg-yellow-50 text-yellow-600" },
                { label: "Revenus confirmés", value: formatPrice(stats.revenue), icon: TrendingUp, color: "bg-green-50 text-green-600" },
              ].map(({ label, value, icon: Icon, color }) => (
                <div key={label} className="bg-white border border-gray-200 rounded-md p-3" data-testid={`stat-${label.toLowerCase()}`}>
                  <div className={`w-8 h-8 rounded-md ${color} flex items-center justify-center mb-2`}>
                    <Icon size={16} />
                  </div>
                  <p className="text-lg font-black text-[#232F3E]">{value}</p>
                  <p className="text-xs text-gray-500">{label}</p>
                </div>
              ))}
            </div>

            {/* Analytics leads */}
            <div className="bg-white border border-gray-200 rounded-md p-4 mb-4">
              <div className="flex items-center gap-2 mb-3">
                <BarChart2 size={15} className="text-[#FF9900]" />
                <h3 className="text-sm font-bold text-[#232F3E]">Funnel WhatsApp-first</h3>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
                <div className="text-center bg-blue-50 rounded-md p-3">
                  <p className="text-2xl font-black text-blue-600">{analytics?.product_views ?? profile?.leads_count ?? 0}</p>
                  <p className="text-xs text-gray-500 mt-0.5">Vues produits</p>
                </div>
                <div className="text-center bg-orange-50 rounded-md p-3">
                  <p className="text-2xl font-black text-orange-600">{analytics?.click_orders ?? 0}</p>
                  <p className="text-xs text-gray-500 mt-0.5">Clics Commander</p>
                </div>
                <div className="text-center bg-purple-50 rounded-md p-3">
                  <p className="text-2xl font-black text-purple-600">{analytics?.orders_initiated ?? 0}</p>
                  <p className="text-xs text-gray-500 mt-0.5">Intentions</p>
                </div>
                <div className="text-center bg-green-50 rounded-md p-3">
                  <p className="text-2xl font-black text-green-600">{analytics?.payments_declared ?? 0}</p>
                  <p className="text-xs text-gray-500 mt-0.5">Paiements déclarés</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="text-center bg-gray-50 rounded-md p-3">
                  <p className="text-2xl font-black text-[#232F3E]">{analytics?.total_leads ?? orders.length}</p>
                  <p className="text-xs text-gray-500 mt-0.5">Total leads</p>
                </div>
                <div className="text-center bg-gray-50 rounded-md p-3">
                  <p className="text-2xl font-black text-green-600">
                    {analytics?.conversion_rate ?? (orders.length > 0
                      ? Math.round(orders.filter((o) => ["paid", "livré"].includes(o.status)).length / orders.length * 100)
                      : 0)}%
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5">Taux de conversion</p>
                </div>
              </div>
            </div>

            {/* Plan abonnement */}
            {subStatus?.pending_request?.status === "pending" ? (
              <div className="bg-amber-50 border border-amber-200 rounded-md p-4 flex items-center gap-3">
                <Clock size={18} className="text-amber-500 shrink-0" />
                <div>
                  <p className="font-bold text-sm text-amber-800">Paiement en cours de vérification</p>
                  <p className="text-xs text-amber-600">Plan {subStatus.pending_request.plan} — Notre équipe valide votre paiement sous 24h.</p>
                </div>
              </div>
            ) : (subStatus?.current_plan === "FREE" || !subStatus) && (
              <div className="bg-gradient-to-r from-[#232F3E] to-[#37475A] text-white rounded-md p-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="font-bold text-sm flex items-center gap-1.5 mb-1">
                      <Zap size={14} className="text-[#FF9900]" /> Vous avez atteint votre limite
                    </p>
                    <p className="text-xs text-gray-300 mb-0.5">Passez PRO pour recevoir plus de clients — 10 000 FCFA/mois</p>
                    <p className="text-xs text-gray-400">Paiement Wave ou Orange Money · Numéro : 77 338 90 95</p>
                  </div>
                  <button
                    onClick={() => setShowSubModal(true)}
                    className="shrink-0 bg-[#FF9900] text-[#111827] font-bold px-3 py-2 rounded-md text-xs hover:bg-[#E68A00]"
                    data-testid="upgrade-pro-btn"
                  >
                    Passer PRO
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Products */}
        {tab === "products" && (
          <div>
            <div className="flex justify-between items-center mb-3">
              <p className="text-sm text-gray-600">{products.length} produit{products.length > 1 ? "s" : ""}</p>
              <button
                onClick={() => { setShowProductForm(true); setEditingProduct(null); setProductForm({ name: "", description: "", price: "", stock: "1", category_id: "", images: [] }); }}
                className="flex items-center gap-1.5 bg-[#FF9900] text-[#111827] text-sm font-bold px-3 py-2 rounded-md hover:bg-[#E68A00] transition-colors"
                data-testid="add-product-btn"
              >
                <Plus size={14} /> Ajouter un produit
              </button>
            </div>

            {showProductForm && (
              <div className="bg-white border border-gray-200 rounded-md p-4 mb-4">
                <h3 className="font-extrabold text-[#232F3E] mb-3 text-sm">
                  {editingProduct ? "Modifier le produit" : "Nouveau produit"}
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-semibold text-gray-600 mb-1 block">Nom du produit *</label>
                    <input value={productForm.name} onChange={(e) => setProductForm((p) => ({ ...p, name: e.target.value }))} placeholder="Ex: iPhone 13 Pro" className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-[#FF9900] outline-none" data-testid="product-name-input" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-600 mb-1 block">Prix (FCFA) *</label>
                    <input type="number" value={productForm.price} onChange={(e) => setProductForm((p) => ({ ...p, price: e.target.value }))} placeholder="0" className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-[#FF9900] outline-none" data-testid="product-price-input" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-600 mb-1 block">Stock</label>
                    <input type="number" value={productForm.stock} onChange={(e) => setProductForm((p) => ({ ...p, stock: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-[#FF9900] outline-none" data-testid="product-stock-input" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-600 mb-1 block">Catégorie</label>
                    <select value={productForm.category_id} onChange={(e) => setProductForm((p) => ({ ...p, category_id: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-[#FF9900] outline-none bg-white" data-testid="product-category-select">
                      <option value="">Sélectionner...</option>
                      {categories.map((c) => <option key={c._id} value={c._id}>{c.name}</option>)}
                    </select>
                  </div>
                  <div className="sm:col-span-2">
                    <label className="text-xs font-semibold text-gray-600 mb-1 block">Description</label>
                    <textarea value={productForm.description} onChange={(e) => setProductForm((p) => ({ ...p, description: e.target.value }))} rows={3} placeholder="Décrivez votre produit..." className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-[#FF9900] outline-none resize-none" data-testid="product-description-input" />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="text-xs font-semibold text-gray-600 mb-1 block">Images</label>
                    <div className="flex flex-wrap gap-2 mb-2">
                      {productForm.images.map((img, i) => (
                        <div key={i} className="relative w-16 h-16">
                          <img src={getImageUrl(img)} alt="" className="w-16 h-16 object-cover rounded border border-gray-200" onError={(e) => { e.target.src = "https://via.placeholder.com/64"; }} />
                          <button onClick={() => setProductForm((p) => ({ ...p, images: p.images.filter((_, j) => j !== i) }))} className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white rounded-full flex items-center justify-center">
                            <X size={10} />
                          </button>
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={() => fileRef.current?.click()}
                        disabled={uploadingImg}
                        className="w-16 h-16 border-2 border-dashed border-gray-300 rounded flex flex-col items-center justify-center text-gray-400 hover:border-[#FF9900] hover:text-[#FF9900] transition-colors"
                        data-testid="upload-image-btn"
                      >
                        {uploadingImg ? <div className="w-4 h-4 border-2 border-[#FF9900] border-t-transparent rounded-full animate-spin" /> : <><Upload size={14} /><span className="text-[9px] mt-0.5">Photo</span></>}
                      </button>
                      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && handleUploadImage(e.target.files[0])} />
                    </div>
                  </div>
                </div>
                <div className="flex gap-2 mt-3">
                  <button onClick={handleSaveProduct} disabled={saving} className="bg-[#FF9900] text-[#111827] font-bold px-4 py-2 rounded-md text-sm hover:bg-[#E68A00] disabled:opacity-60" data-testid="save-product-btn">
                    {saving ? "Enregistrement..." : editingProduct ? "Modifier" : "Publier"}
                  </button>
                  <button onClick={() => { setShowProductForm(false); setEditingProduct(null); }} className="border border-gray-300 text-gray-600 font-medium px-4 py-2 rounded-md text-sm hover:bg-gray-50">
                    Annuler
                  </button>
                </div>
              </div>
            )}

            {products.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-md border border-gray-200">
                <Package size={40} className="text-gray-300 mx-auto mb-2" />
                <p className="text-gray-500 text-sm">Aucun produit. Ajoutez votre premier produit !</p>
              </div>
            ) : (
              <div className="space-y-2">
                {products.map((p) => (
                  <div key={p._id} className="bg-white border border-gray-200 rounded-md p-3 flex items-center gap-3" data-testid="vendor-product-item">
                    <div className="w-12 h-12 rounded bg-gray-100 shrink-0 overflow-hidden">
                      {p.images?.[0] ? <img src={getImageUrl(p.images[0])} alt={p.name} className="w-full h-full object-cover" onError={(e) => { e.target.src = "https://via.placeholder.com/48"; }} /> : <Package size={20} className="text-gray-300 m-auto mt-3" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm text-[#232F3E] truncate">{p.name}</p>
                      <p className="text-xs text-gray-500">{formatPrice(p.price)} · Stock: {p.stock}</p>
                    </div>
                    <div className="flex gap-1.5 shrink-0">
                      <button onClick={() => openEdit(p)} className="p-1.5 text-blue-500 hover:bg-blue-50 rounded transition-colors" data-testid="edit-product-btn">
                        <Edit3 size={14} />
                      </button>
                      <button onClick={() => handleDeleteProduct(p._id)} className="p-1.5 text-red-500 hover:bg-red-50 rounded transition-colors" data-testid="delete-product-btn">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Orders */}
        {tab === "orders" && (
          <div className="space-y-3">
            {orders.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-md border border-gray-200">
                <ShoppingBag size={40} className="text-gray-300 mx-auto mb-2" />
                <p className="text-gray-500 text-sm">Aucune commande reçue</p>
              </div>
            ) : orders.map((order) => (
              <div key={order._id} className="bg-white border border-gray-200 rounded-md p-4" data-testid="vendor-order-item">
                <div className="flex items-center justify-between mb-2">
                  <p className="font-bold text-sm text-[#232F3E]">#{order._id?.slice(-6).toUpperCase()}</p>
                  <span className={`text-xs font-medium px-2 py-0.5 rounded ${STATUS_COLORS[order.status] || "bg-gray-100 text-gray-600"}`}>
                    {STATUS_LABELS[order.status] || order.status}
                  </span>
                </div>

                {/* PENDING : validation en cours (ancien flux admin) */}
                {order.status === "pending" ? (
                  <div className="flex items-center gap-2 text-xs bg-amber-50 border border-amber-200 rounded px-3 py-2 mb-3" data-testid="pending-validation-badge">
                    <Clock size={12} className="text-amber-500 shrink-0" />
                    <span className="text-amber-700 font-semibold">Validation Jokkoleen en cours — informations client masquées</span>
                  </div>
                ) : ["awaiting_payment", "initiated"].includes(order.status) ? (
                  <div className="mb-2">
                    <div className="flex items-center gap-2 text-xs bg-blue-50 border border-blue-200 rounded px-3 py-2 mb-2" data-testid="awaiting-payment-badge">
                      <Clock size={12} className="text-blue-500 shrink-0" />
                      <span className="text-blue-700 font-semibold">
                        {order.status === "initiated" ? "Client a saisi son numéro — paiement en attente" : "En attente du paiement client"}
                      </span>
                    </div>
                    {!order.vendor_blocked && (
                      <div className="flex items-center gap-1.5 text-xs text-gray-600 mb-1">
                        <Phone size={10} className="text-gray-400 shrink-0" />
                        <span className="font-medium">{order.customer_name}</span>
                        {order.customer_phone && <span>· {order.customer_phone}</span>}
                        <a
                          href={`https://wa.me/${formatWhatsApp(order.customer_phone)}?text=${encodeURIComponent(`Bonjour, je vous contacte suite à votre demande Jokkoleen #${order._id?.slice(-6).toUpperCase()}`)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="ml-auto flex items-center gap-1 bg-[#25D366] text-white px-2 py-0.5 rounded font-bold hover:bg-[#20BD5A]"
                          data-testid="whatsapp-customer-btn"
                        >
                          <MessageCircle size={10} /> WA
                        </a>
                      </div>
                    )}
                  </div>
                ) : order.status === "payment_declared" ? (
                  <div className="mb-2">
                    <div className="flex items-center gap-2 text-xs bg-teal-50 border border-teal-200 rounded px-3 py-2 mb-2" data-testid="payment-declared-badge">
                      <CheckCircle size={12} className="text-teal-500 shrink-0" />
                      <span className="text-teal-700 font-semibold">Client a déclaré son paiement — vérifiez et expédiez !</span>
                    </div>
                    {!order.vendor_blocked && (
                      <div className="flex items-center gap-1.5 text-xs text-gray-600 mb-1">
                        <Phone size={10} className="text-gray-400 shrink-0" />
                        <span className="font-medium">{order.customer_name}</span>
                        {order.customer_phone && <span>· {order.customer_phone}</span>}
                        <a
                          href={`https://wa.me/${formatWhatsApp(order.customer_phone)}?text=${encodeURIComponent(`Bonjour, j'ai bien reçu votre paiement pour la commande #${order._id?.slice(-6).toUpperCase()} sur Jokkoleen. Je prépare votre commande !`)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="ml-auto flex items-center gap-1 bg-[#25D366] text-white px-2 py-0.5 rounded font-bold hover:bg-[#20BD5A]"
                          data-testid="whatsapp-customer-btn"
                        >
                          <MessageCircle size={10} /> WA
                        </a>
                      </div>
                    )}
                  </div>
                ) : (
                  <>
                    {/* Lead bloqué — pay-per-lead */}
                    {order.vendor_blocked ? (
                      <div className="bg-orange-50 border border-orange-200 rounded p-3 mb-2" data-testid="blocked-lead-banner">
                        <p className="text-xs font-bold text-orange-700 mb-1 flex items-center gap-1">
                          <Lock size={11} /> Lead bloqué — quota FREE dépassé
                        </p>
                        <p className="text-xs text-orange-600 mb-2">Débloquez ce lead pour 200 FCFA via Wave/Orange Money</p>
                        <a
                          href={`https://wa.me/${ADMIN_WA}?text=${encodeURIComponent(`Bonjour Jokkoleen, je veux débloquer le lead #${order._id?.slice(-6).toUpperCase()} (200 FCFA). Boutique: ${profile?.shop_name}`)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 bg-orange-500 text-white text-xs font-bold px-3 py-1.5 rounded hover:bg-orange-600"
                          data-testid="unlock-lead-btn"
                        >
                          <MessageCircle size={11} /> Débloquer ce lead (200 FCFA)
                        </a>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1.5 text-xs text-gray-600 mb-1">
                        <Phone size={10} className="text-gray-400 shrink-0" />
                        <span className="font-medium">{order.customer_name}</span>
                        {order.customer_phone && <span>· {order.customer_phone}</span>}
                        <a
                          href={`https://wa.me/${formatWhatsApp(order.customer_phone)}?text=${encodeURIComponent(`Bonjour, je vous contacte suite à votre demande Jokkoleen #${order._id?.slice(-6).toUpperCase()}`)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="ml-auto flex items-center gap-1 bg-[#25D366] text-white px-2 py-0.5 rounded font-bold hover:bg-[#20BD5A]"
                          data-testid="whatsapp-customer-btn"
                        >
                          <MessageCircle size={10} /> WA
                        </a>
                      </div>
                    )}
                  </>
                )}

                <p className="text-xs text-gray-400 mb-1">{new Date(order.created_at).toLocaleDateString("fr-FR")}</p>
                <p className="font-black text-[#232F3E] text-sm mb-3">{formatPrice(order.total_amount)}</p>

                {/* Boutons d'action — uniquement si statut actionnable */}
                {!["pending", "awaiting_payment", "initiated"].includes(order.status) && (
                  <div className="flex flex-wrap gap-1.5 border-t border-gray-100 pt-2">
                    {(VENDOR_NEXT_STATES[order.status] || []).map(({ value, label, danger }) => (
                      <button
                        key={value}
                        onClick={async () => {
                          try {
                            await API.put(`/api/orders/${order._id}/status`, { status: value });
                            setOrders((prev) => prev.map((o) => o._id === order._id ? { ...o, status: value } : o));
                          } catch (e) {
                            alert(formatError(e));
                          }
                        }}
                        className={`text-xs px-3 py-1.5 rounded border font-semibold transition-colors ${
                          danger
                            ? "bg-red-50 text-red-600 border-red-200 hover:bg-red-100"
                            : "bg-green-50 text-green-700 border-green-200 hover:bg-green-100"
                        }`}
                        data-testid={`status-btn-${value}`}
                      >
                        {label}
                      </button>
                    ))}
                    {(VENDOR_NEXT_STATES[order.status] || []).length === 0 && (
                      <span className="text-xs text-gray-400 italic py-1">Commande terminée</span>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Profile */}
        {tab === "profile" && (
          <div className="bg-white border border-gray-200 rounded-md p-4">
            <h3 className="font-extrabold text-[#232F3E] mb-4 text-sm">Informations de la boutique</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                { key: "shop_name", label: "Nom de la boutique", placeholder: "Mon Super Shop" },
                { key: "description", label: "Description", placeholder: "Décrivez votre boutique...", type: "textarea" },
                { key: "phone_wave", label: "Numéro Wave", placeholder: "77 123 45 67" },
                { key: "phone_orange", label: "Numéro Orange Money", placeholder: "77 987 65 43" },
                { key: "whatsapp_number", label: "Numéro WhatsApp", placeholder: "+221 77 123 45 67" },
                { key: "shipping_delay", label: "Délai de livraison", placeholder: "2-3 jours ouvrés" },
                { key: "avatar", label: "URL du logo / photo", placeholder: "https://..." },
              ].map(({ key, label, placeholder, type }) => (
                <div key={key} className={type === "textarea" ? "sm:col-span-2" : ""}>
                  <label className="text-xs font-semibold text-gray-600 mb-1 block">{label}</label>
                  {type === "textarea" ? (
                    <textarea value={profileForm[key] || ""} onChange={(e) => setProfileForm((p) => ({ ...p, [key]: e.target.value }))} rows={3} placeholder={placeholder} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-[#FF9900] outline-none resize-none" data-testid={`profile-${key}`} />
                  ) : (
                    <input type="text" value={profileForm[key] || ""} onChange={(e) => setProfileForm((p) => ({ ...p, [key]: e.target.value }))} placeholder={placeholder} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-[#FF9900] outline-none" data-testid={`profile-${key}`} />
                  )}
                </div>
              ))}
              <div className="sm:col-span-2">
                <label className="text-xs font-semibold text-gray-600 mb-1 block">Zones de livraison (séparées par des virgules)</label>
                <input
                  type="text"
                  value={(profileForm.shipping_zones || []).join(", ")}
                  onChange={(e) => setProfileForm((p) => ({ ...p, shipping_zones: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) }))}
                  placeholder="Dakar, Thiès, Saint-Louis..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-[#FF9900] outline-none"
                  data-testid="profile-shipping-zones"
                />
              </div>
            </div>
            {profileMsg && <p className={`text-xs mt-2 p-2 rounded ${profileMsg.includes("succès") ? "bg-green-50 text-green-700" : "bg-red-50 text-red-600"}`}>{profileMsg}</p>}
            <button onClick={handleSaveProfile} disabled={savingProfile} className="mt-4 bg-[#FF9900] text-[#111827] font-bold px-5 py-2.5 rounded-md hover:bg-[#E68A00] text-sm disabled:opacity-60" data-testid="save-profile-btn">
              {savingProfile ? "Enregistrement..." : "Sauvegarder le profil"}
            </button>
          </div>
        )}
      </div>

      {/* Subscription Modal */}
      {showSubModal && (
        <SubscriptionModal
          currentPlan={subStatus?.current_plan || profile?.plan || "FREE"}
          pendingRequest={subStatus?.pending_request}
          onClose={() => setShowSubModal(false)}
          onSuccess={() => {
            API.get("/api/vendor/subscription-status").then((r) => setSubStatus(r.data));
          }}
        />
      )}
    </div>
  );
}
